import { applyPercentageDepositRounding } from '../../../shared/functions/apply-percentage-deposit-rounding.function';
import { toRoundedAmount } from '../../../shared/functions/to-rounded-amount.function';
import { DepositPolicyTypeEnum } from '../enums/deposit-policy-type.enum';
import { DepositPolicyApplyInput } from '../interfaces/deposit-policy-apply-input.interface';
import { DepositPolicyConfig } from '../interfaces/deposit-policy-config.interface';
import { PolicyApply } from '../interfaces/policy-apply.interface';

/**
 * Applies deposit policy config to produce a deposit amount.
 *
 * - No policy (`config` null/undefined): `0`.
 * - `fixed`: cents-round only (no increment rounding).
 * - `percentage`: % of discounted standard gross, then percentage deposit rounding.
 * - `days_worth`: daysWorth * dayRateGross (cents only; 0 if day rate missing).
 * Optional min/max clamps apply after the type-specific amount is computed.
 */
export const DepositPolicy: PolicyApply<
  DepositPolicyApplyInput,
  number,
  DepositPolicyConfig
> = {
  apply(
    input: DepositPolicyApplyInput,
    config: DepositPolicyConfig | null | undefined,
  ): number {
    if (config == null) {
      return 0;
    }

    let amount = 0;

    switch (config.depositType) {
      case DepositPolicyTypeEnum.FIXED: {
        amount = toRoundedAmount(config.fixedAmount);
        break;
      }
      case DepositPolicyTypeEnum.PERCENTAGE: {
        const pct = Number(config.percentageOfRental ?? 0);
        const raw = Number(input.discountedStandardGross ?? 0) * (pct / 100);
        amount = toRoundedAmount(
          applyPercentageDepositRounding(raw, config.percentageDepositRounding),
        );
        break;
      }
      case DepositPolicyTypeEnum.DAYS_WORTH: {
        const days = Number(config.daysWorth ?? 0);
        const dayRate = Number(input.dayRateGross ?? 0);
        if (!(days > 0) || !(dayRate > 0)) {
          amount = 0;
        } else {
          amount = toRoundedAmount(days * dayRate);
        }
        break;
      }
      default:
        amount = 0;
    }

    if (config.minimumDeposit != null && amount < config.minimumDeposit) {
      amount = toRoundedAmount(config.minimumDeposit);
    }
    if (config.maximumDeposit != null && amount > config.maximumDeposit) {
      amount = toRoundedAmount(config.maximumDeposit);
    }

    return amount;
  },
};
